<template>
  <div
    class="relative isolate z-0 flex flex-col select-none rounded-md border border-charcoal-200 cursor-pointer hover:z-20 focus-within:z-30"
    :class="selected && 'bg-shell-200'"
    @click="itemClicked"
  >

    <div
      class="absolute inset-0 z-10 opacity-0 transition-opacity cursor-pointer"
      :class="{'opacity-25 bg-blue': selectionCandidate}"
      @click.stop="toggleSelected"
      v-if="hasAny"
    />

    <div
      v-if="!hideCover"
      class="aspect-[6/5] isolate relative rounded-t-md overflow-hidden"
      :class="[
        !disableEdit && 'cursor-pointer',
        selected ? 'bg-shell-300' : 'bg-shell-200'
      ]">

      <!--image states-->
      <transition-group name="f-fade">
        <u-icon
          v-if="!src"
          :name="COMMON_ICONS.tag"
          class="absolute-center text-charcoal/60 opacity-30 w-24 h-24"
          key="place"
        />
        <span v-else-if="isLoading" class="absolute-center w-16 h-16" key="load">
          <u-icon
            :name="COMMON_ICONS.loading"
            class="text-charcoal/60 opacity-5 w-full h-full animate-spin"
          />
        </span>
        <u-icon
          v-else-if="error"
          :name="COMMON_ICONS.photo"
          class="absolute-center text-charcoal/60 opacity-30 w-16 h-16"
          key="err"
          title="something went wrong when loading this image"
        />
        <div
          v-else
          class="absolute bg-no-repeat bg-center transition-all"
          key="final"
          :style="{backgroundImage: `url(${src})`}"
          :class="[
            selected ? 'inset-2 rounded-lg' : 'inset-0',
            isProject ? 'bg-contain' : 'bg-cover'
          ]"
        />
      </transition-group>
    </div>

    <div class="p-2 flex flex-col flex-1 gap-2">
      <div class="flex justify-between w-full gap-2 h-[30px]">
        <div v-if="selectable" class="relative z-20" @click.stop>
          <tag-select-toggle dark v-model:selected="selected" />
        </div>
        <div class="flex gap-2">
          <div class="flex-center">
            <core-list-item-stats
              :container="tag"
            />
          </div>
          <div v-if="!disableEdit && !hasAny" @click.stop>
            <tag-list-item-context-menu
              :tag="tag"
              v-show="!hasAny"
            />
          </div>
        </div>
      </div>

      <div>

        <div class="inline-flex" @click.stop>
          <tag-rename-merge :tag="tag" @merged="onMerged" :disabled="disableEdit">
          <span
            :class="[
              'font-semibold break-long-string leading-tight',
              textSize,
              {'cursor-pointer hover:underline': !disableEdit}
            ]"
            :title="tag.name"
          >
            {{tag.name}}
          </span>
          </tag-rename-merge>
        </div>
      </div>

    </div>

  </div>
</template>

<script setup>
  import {useImage} from '@vueuse/core';

  const emit = defineEmits(['item-click', 'merged']);
  const props = defineProps({
    tag: Object,
    selectable: {
      type: Boolean,
      default: true
    },
    selectionCandidate: Boolean,
    disableEdit: Boolean,
    hideCover: Boolean,
    textSize: {
      type: String,
      default: 'text-base'
    }
  });

  const selected = defineModel('selected');
  const {hasAny} = useSelection();
  const src = computed(() => props.tag.avatarImage);
  const isProject = computed(() => src.value.includes('/projects/'));

  const { isLoading, error } = src.value ? useImage({src: src.value}) : {};

  function onMerged({person}) {
    emit('merged', {person});
  }

  function itemClicked() {
    emit('item-click', props.tag);
  }

  function toggleSelected() {
    if (!props.selectable || !hasAny.value) return;
    selected.value = !selected.value;
  }

</script>
